/**
 * Cross-page data-shape mappings — API field values → design-system display
 * vocabulary (Badge variants, StatusPill statuses, human labels). Shared by the
 * pages that render the same domain enums (niche status on the dashboard +
 * niches list; audit action colouring on the dashboard's recent-events table +
 * the audit log). Page-specific maps (ticket/user status) stay local to their
 * page hooks.
 */

import type {
  DuviSharedAdminBadgeVariant,
  DuviSharedAdminStatusPillStatus,
} from "@duvi-agency/ui";
import type { NicheSummary } from "../api/client";

// ── Niche status ─────────────────────────────────────────────────────────────
export function duviCoreAdminNicheStatusPill(
  status: NicheSummary["status"],
): DuviSharedAdminStatusPillStatus {
  switch (status) {
    case "active":
      return "success";
    case "provisioning":
      return "warning";
    case "suspended":
      return "error";
    case "tearing_down":
      return "inactive";
    default:
      return "inactive";
  }
}

export function duviCoreAdminNicheStatusLabel(
  status: NicheSummary["status"],
): string {
  switch (status) {
    case "active":
      return "Active";
    case "provisioning":
      return "Provisioning";
    case "suspended":
      return "Suspended";
    case "tearing_down":
      return "Tearing down";
    default:
      return status;
  }
}

// ── Audit action → Badge variant ──────────────────────────────────────────────
// Handles the API's dotted action strings (e.g. "kb_article.created",
// "user.invited") by matching on the verb stem, mirroring the export's
// colour-coded action pills.
export function duviCoreAdminAuditActionVariant(
  action: string,
): DuviSharedAdminBadgeVariant {
  const a = (action || "").toLowerCase();
  if (a.includes("creat") || a.includes("invit") || a.includes("add"))
    return "success";
  if (a.includes("delet") || a.includes("remov")) return "error";
  if (a.includes("disabl") || a.includes("block") || a.includes("suspend"))
    return "error";
  if (a.includes("updat") || a.includes("chang") || a.includes("edit"))
    return "info";
  if (a.includes("publish") || a.includes("push")) return "primary";
  if (a.includes("login") || a.includes("sign")) return "neutral";
  if (a.includes("export")) return "accent";
  if (a.includes("archiv") || a.includes("reset")) return "warning";
  if (a.includes("resolv") || a.includes("enabl")) return "success";
  return "neutral";
}
